import React, { useState, useEffect } from 'react';
import { useRecoilStateLoadable, useRecoilValue } from 'recoil';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import colors from '../../../assets/theme/base/colors';
import { ScorecardListState } from '../../../State/ScorecardSelection.Atom';
import { UserDataTable } from '../../Base/UserManagement/UserDataTable';
import { EEUserScorecardDataGrid } from '../../Base/UserManagement/UserScorecardDataColumns';
import { GridSelectionModel } from '@mui/x-data-grid';
import { EEScorecard } from '../../../Component/Model/Dto/EmploymentEquity/EEScorecard';
import { ColumnSortOption } from '../../../Component/Model/Dto/ColumnSortOption';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { EEScorecardService } from '../../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService';
import { ScorecardAtom } from '../../../State/Scorecard.Atom';
import { HistoryTableMapper } from './ConsolidationHistory/ConsolidationHistoryTable';
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { MDButton } from '../../../Component/Common/Components/MDButton';
import { LinearProgressWithLabel } from '../GeneralInformationPage/GeneralInformation';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { ScorecardMeta } from '../../../Component/Model/Dto/Scorecard/ScorecardMeta';
import {ConsolidationRules} from "./ConsolidationRules";
import {defaultObject} from "../../../Helpers/typescriptHelper";
import {ConsolidationResultsModal} from "./ConsolidationConflicts/ConsolidationResultsModal";
import {Grid} from "@mui/material";
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

interface SelectedScorecards {
    id: string;
    scorecardName: string;
}

const { dark } = colors;
export const Reconsolidation = () => {
    const [EEUserScorecardData, SetEEUserScorecardData] = useRecoilStateLoadable(ScorecardListState);
    const [Progress, setProgress] = useState(0);
    const scorecard = useRecoilValue(ScorecardAtom);
    const [EEScorecardSelectionModel, setEEScorecardSelectionModel] = React.useState<GridSelectionModel>([]);
    const [EESelectionLength, setEESelectionLength] = React.useState(0);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [HistoryData, setHistoryData] = useState<EEScorecard.History[]>([]);
    const [metaData, setMetaData] = useState<EEScorecard.MetaDto>(defaultObject);
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [listOfScorecards, setListOfScorecards] = useState<string[]>([]);
    const [showRulesPage, setShowRulesPage] = React.useState(false);
    const [SelectedScorecards, setSelectedScorecards] = React.useState<SelectedScorecards[]>([]);
    const [conflictsPopup, setConflictsPopup] = React.useState<boolean>(false);
    const [selectedConsolidationId, setConsolidationId] = React.useState<number>(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const hub = useSignalRConnection("ScorecardCopyHub");
    const confirmOverrideMessage = "Please note, if you reconsolidate/update your Consolidated Scorecard, all the data will be deleted and replaced with updated data from the selected source scorecards. Are you sure you want to continue?";

    const refreshTable = () => {
        setRefreshKey((prev) => prev + 1);
    };

    let consolidationHistoryRequest: EEScorecard.HistorySearchDtoIn = {
        columnSortOptions: columnSortOptions,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }
    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }
    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {

            let resp = await EEScorecardService.SearchConsolidationHistory(consolidationHistoryRequest, scorecard.scorecardId);
            if (resp.data) {
                setHistoryData(resp.data.results);
                setFilteredItemsCount(resp.data.filteredItemsCount);
                setTotalItemsCount(resp.data.totalItemsCount);
            }

            let meta = await  EEScorecardService.GetScorecardMeta(scorecard.scorecardId);
            if(meta.data)
            {
                setMetaData(meta.data)
            }
        } catch (error) {
            console.log(error);
            setFilteredItemsCount(0);
            setTotalItemsCount(0);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchSelectedScorecard = async () => {
        try {
            const results = await EEScorecardService.ScorecardsInConsolidation(scorecard.scorecardId);
            setListOfScorecards(results.data);
            setEEScorecardSelectionModel([...results.data]);
            setEESelectionLength(EEScorecardSelectionModel.length);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        fetchSelectedScorecard();
    }, [scorecard.scorecardId]);

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions, refreshKey]);

    useEffect(() => {
        if (EEUserScorecardData.state === "hasValue") {
            const filteredData = EEUserScorecardData.contents.filter((item: ScorecardMeta.ScorecardMetaViewModel) => item.ID !== scorecard.scorecardId);
            SetEEUserScorecardData(filteredData);
        }
    }, [EEUserScorecardData.state]);

    useEffect(() => {
        // compare array EEScorecardSelectionModel to listOfScorecards
        if (_.isEqual(EEScorecardSelectionModel.sort(), listOfScorecards.sort())) {
            setDisableButton(false);
        }else {
            setDisableButton(true);
        }

    }, [EEScorecardSelectionModel])

    useEffect(() => {
        if (!scorecard?.scorecardId) return;

        (async () => {
            try {
                if(!hub.isConnected)
                {
                    await hub.start();
                }

                const identifier = `consolidation-${scorecard.scorecardId}`;

                hub.on(`taskStarted-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });

                hub.on(`taskProgressChanged-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });

                hub.on(`taskEnded-${identifier}`, (percent: number) => {
                    setProgress(percent);
                });
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, [scorecard.scorecardId]);
    
    const validateScorecardSelection = () => {
        const minimumScorecards = 2;
        if (EEScorecardSelectionModel.length < minimumScorecards) {
            toast.error('Please ensure two or more scorecards are selected.');
            return false;
        }
        return true;
    }

    const startReconsolidation = async () => {
        if (!validateScorecardSelection()) {
            return;
        }
        
        try {
            setDisableButton(true);
            const result = await EEScorecardService.ReConsolidateScorecard(scorecard.scorecardId);

            if (result.status === 200) {
                setProgress(100);
                fetchData();

                result.data.wasSuccessful 
                    ? toast.success('Consolidation completed successfully.')
                    : toast.warning('Consolidation completed with conflicts. Please resolve them');              
            }
        } catch (error) {
            console.log(error);
        }finally {
            setDisableButton(false);
        }
    }

    const setConsolidationRules = async () => {
        if (!validateScorecardSelection()) {
            return;
        }

        setSelectedScorecards(mapScorecardIdToName(EEScorecardSelectionModel));
        setShowRulesPage(true);
    }

    const handleConflicts = (consolidationId: number) => {
        setConsolidationId(consolidationId);
        setConflictsPopup(true);
    }

    const handleClosePopup = () => {
        setConflictsPopup(false);
        setConsolidationId(0);
        setRefreshKey((prevKey) => prevKey + 1);    
    }

    const mapScorecardIdToName = (SelectedIds: GridSelectionModel) => {
        let mappedScorecards: SelectedScorecards[] = [];
        SelectedIds.forEach((id) => {
            mappedScorecards.push({
                id: id.toString(),
                scorecardName: EEUserScorecardData.contents.find((scorecard: ScorecardMeta.ScorecardMetaViewModel) => scorecard.ID === id)?.Name
            })
        });
        return mappedScorecards;
    }

    const historyTableData = HistoryTableMapper(HistoryData, handleConflicts);
    if (historyTableData === undefined) {
        return (
            <SkeletonLoading formLoading FieldAmount={6} />
        );
    }

    const handleReconsolidateClick = () => {
        if (!validateScorecardSelection()) return;
        setShowConfirmationDialog(true); 
    }

    const confirmReconsolidation = async () => {
        setShowConfirmationDialog(false);
        await startReconsolidation();
    }

    const hideRulesPage = () => {
        setShowRulesPage(false);
    }

    if (showRulesPage) {
        return <ConsolidationRules 
            metaData={metaData} 
            selectedScorecards={SelectedScorecards} 
            previous={() => {
                hideRulesPage();
                refreshTable();
            }}             
            key={metaData.scorecardId}
        />
    }

    return (
        <>
            <AppAccordion label='Scorecards' expandOnDefault={true} backgroundColor='white' iconFill={dark.main} textColor={dark.main}>
                 <UserDataTable
                    columns={EEUserScorecardDataGrid.columns}
                    isLoading={EEUserScorecardData.state === 'loading'}
                    rows={EEUserScorecardData.contents}
                    selectionModel={EEScorecardSelectionModel}
                    setSelectionModel={setEEScorecardSelectionModel}
                    checkboxSelection={true}
                />
            </AppAccordion>
            <Grid container spacing={2} pt={2}>
                <Grid item xs={10} justifyContent={'flex-start'}>
                    <MDButton variant="gradient" color="info" onClick={handleReconsolidateClick} disabled={disableButton}>Reconsolidate</MDButton>
                </Grid>
                <Grid item xs={2} justifyContent={'flex-end'}>
                    <MDButton variant="gradient" color="info" onClick={() => setConsolidationRules()}>Consolidation Rules</MDButton>
                </Grid>
            </Grid>
            <LinearProgressWithLabel value={Progress} />
            <DataTable
                key={refreshKey}
                table={historyTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                columnSortingChanged={columnSortingChanged}
            ></DataTable>

            {conflictsPopup && <ConsolidationResultsModal
                consolidationId={selectedConsolidationId}
                scorecardId={scorecard.scorecardId}
                showConsolidationResultsModal={conflictsPopup}
                closeConsolidationResultsModal={handleClosePopup}/>
            }

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={confirmReconsolidation}
                handleCancel={() => setShowConfirmationDialog(false)}
                heading="Confirm Reconsolidation"
                message={confirmOverrideMessage}
            />
        </>
    )
}

